// src/modules/ml/ClusteringLab.jsx
// Clustering Lab — standalone ML sidebar module
// k-Means++ · Elbow Method · Silhouette · DBSCAN · Hierarchical · 2D Viz

import React, { useState, useRef, memo, useCallback } from 'react';
import {
  LineChart, Line, ScatterChart, Scatter, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import {
  parseCSVForML, inferTypes, kMeans, elbowMethod, dbscan, hierarchical,
  generateMockDataset, parseNum, mean,
} from '../../utils/mlProcessing.js';
import { Panel, Metric, Tag, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

const COLORS = ['var(--gold)','var(--cyan)','var(--violet)','var(--green)','var(--red)','var(--orange)','#f472b6','#34d399'];
const Sel = ({value,onChange,options,label,style}) => (<div style={style}>{label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>}<select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>{options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}</select></div>);
const Btn = ({onClick,children,color='var(--gold)',disabled,style}) => (<button onClick={onClick} disabled={disabled} style={{padding:'9px 18px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>);

const TABS = ['upload','setup','kmeans','elbow','dbscan','hierarchical','insights'];

// Extracted outside component so it's never recreated on parent re-render → no blink
const ClusterScatter = memo(function ClusterScatter({scatterData,nClusters,xLabel,yLabel,title,color,showNoise=false,axisX,axisY,featureCols,onAxisChange}) {
  const clusters = [...new Set(scatterData.map(p=>p.cluster))].filter(c=>showNoise?true:c>=0).sort((a,b)=>a-b);
  return (
    <Panel title={title} accent={color}>
      {onAxisChange&&(
        <div style={{display:'flex',gap:'8px',marginBottom:'8px',flexWrap:'wrap'}}>
          <Sel value={axisX} onChange={v=>onAxisChange('x',parseInt(v))} options={featureCols.map((c,i)=>({value:i,label:c}))} label="X AXIS" style={{flex:1}}/>
          <Sel value={axisY} onChange={v=>onAxisChange('y',parseInt(v))} options={featureCols.map((c,i)=>({value:i,label:c}))} label="Y AXIS" style={{flex:1}}/>
        </div>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
          <XAxis dataKey="x" name={xLabel||'x'} stroke="var(--t3)" tick={{fontSize:9}}/>
          <YAxis dataKey="y" name={yLabel||'y'} stroke="var(--t3)" tick={{fontSize:9}}/>
          <Tooltip cursor={{strokeDasharray:'3 3'}} formatter={(v,n)=>[typeof v==='number'?v.toFixed(3):v,n]}/>
          {showNoise&&scatterData.filter(p=>p.cluster===-1).length>0&&(
            <Scatter name="Noise" data={scatterData.filter(p=>p.cluster===-1)} fill="var(--t3)" opacity={0.3} isAnimationActive={false}/>
          )}
          {clusters.filter(c=>c>=0).map(ci=>(
            <Scatter key={ci} name={`Cluster ${ci}`} data={scatterData.filter(p=>p.cluster===ci)} fill={COLORS[ci%COLORS.length]} opacity={0.75} isAnimationActive={false}/>
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </Panel>
  );
});

export default function ClusteringLab() {
  const [tab,setTab] = useState('upload');
  const [data,setData] = useState(null);
  const [logs,setLogs] = useState([{ts:'00:00:00',msg:'Clustering Lab — upload data or choose a sample.',t:'info'}]);
  const [featureCols,setFeatureCols] = useState([]);
  const [axisX,setAxisX] = useState(0); // index into featureCols for scatter x
  const [axisY,setAxisY] = useState(1); // index into featureCols for scatter y
  const [k,setK] = useState(3);
  const [eps,setEps] = useState(1.0);
  const [minPts,setMinPts] = useState(5);
  const [kResult,setKResult] = useState(null);
  const [kScatter,setKScatter] = useState([]);
  const [elbowData,setElbowData] = useState([]);
  const [dbResult,setDbResult] = useState(null);
  const [dbScatter,setDbScatter] = useState([]);
  const [hResult,setHResult] = useState(null);
  const [hScatter,setHScatter] = useState([]);
  const [running,setRunning] = useState(false);
  const fileRef = useRef();
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const loadData = (text,name,direct) => {
    const parsed = direct||(text?parseCSVForML(text):null);
    if(!parsed){log('Parse error','err');return;}
    const types = inferTypes(parsed.headers,parsed.rows);
    const nc = types.filter(t=>t.type==='numeric').map(t=>t.col);
    setData({...parsed,types});
    setFeatureCols(nc.slice(0,3));
    setAxisX(0); setAxisY(Math.min(1,nc.length-1));
    log(`✓ "${name}": ${parsed.rows.length}×${parsed.headers.length} | ${nc.length} numeric features`,'ok');
    setTab('setup');
  };

  const rows = data?.rows||[], types = data?.types||[];
  const nc = types.filter(t=>t.type==='numeric').map(t=>t.col);
  const run = async(label,fn) => {setRunning(true);log(`Running ${label}...`);await new Promise(r=>setTimeout(r,30));fn();setRunning(false);};

  // Build stable scatter data — called once and stored in state
  const buildScatter = useCallback((result, ax=axisX, ay=axisY, fc=featureCols) => {
    if(!result||!fc.length) return [];
    const cx = fc[ax]||fc[0];
    const cy = fc[ay]||fc[0];
    return rows.slice(0,300).map((r,i)=>({
      x: parseNum(r[cx]), y: parseNum(r[cy]),
      cluster: result.labels?.[i]??0,
    })).filter(p=>!isNaN(p.x)&&!isNaN(p.y));
  }, [rows, featureCols, axisX, axisY]);

  const handleAxisChange = (axis, val) => {
    if(axis==='x'){
      setAxisX(val);
      if(kResult) setKScatter(buildScatter(kResult, val, axisY));
      if(dbResult) setDbScatter(buildScatter(dbResult, val, axisY));
      if(hResult) setHScatter(buildScatter(hResult, val, axisY));
    } else {
      setAxisY(val);
      if(kResult) setKScatter(buildScatter(kResult, axisX, val));
      if(dbResult) setDbScatter(buildScatter(dbResult, axisX, val));
      if(hResult) setHScatter(buildScatter(hResult, axisX, val));
    }
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Tabs */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--gold)':'transparent'}`,background:tab===t?'var(--ggold)':'transparent',color:tab===t?'var(--gold)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* UPLOAD */}
      {tab==='upload'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}} onDragOver={e=>e.preventDefault()} onClick={()=>fileRef.current?.click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>⬡</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop CSV File</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Numeric features only — no labels needed for clustering</div>
          </div>
          <Panel title="SAMPLE DATASETS" accent="var(--gold)">
            {[['Clustered Data','clustering','3 natural clusters · 200 rows'],['Housing','housing','4 numeric features · segment properties'],['Linear','linear','2 features · find structure without labels']].map(([name,type,desc])=>(
              <div key={type} onClick={()=>loadData(null,name,generateMockDataset(type))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--gold)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <span style={{fontSize:'18px',color:'var(--gold)'}}>⬡</span>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>{desc}</div></div>
                <span style={{color:'var(--gold)'}}>→</span>
              </div>
            ))}
            <LogPanel logs={logs} height={70}/>
          </Panel>
        </div>
      )}

      {/* SETUP */}
      {tab==='setup'&&data&&(
        <Panel title="FEATURE SELECTION" accent="var(--gold)">
          <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
            Select which numeric features to cluster on. You can choose X/Y axes for scatter plots independently. 
            Tip: normalize features first if they have very different scales.
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'14px'}}>
            {nc.map(col=>(
              <button key={col} onClick={()=>setFeatureCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])}
                style={{padding:'5px 12px',border:`1px solid ${featureCols.includes(col)?'var(--gold)':'var(--b0)'}`,background:featureCols.includes(col)?'var(--ggold)':'var(--raised)',color:featureCols.includes(col)?'var(--gold)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                {col}
              </button>
            ))}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            <Metric label="Features Selected" value={featureCols.length} color="var(--gold)"/>
            <Metric label="Total Rows" value={rows.length} color="var(--cyan)"/>
            <Metric label="Dimensions" value={featureCols.length} color="var(--violet)" sub="for distance computation"/>
            <Metric label="Vis Axes" value="2D scatter" color="var(--t3)"/>
          </div>
          {featureCols.length>2&&<InfoBox color="var(--cyan)" icon="◈" style={{marginTop:'10px'}}>Clustering uses all {featureCols.length} dimensions. Scatter plot shows 2D projection of first 2 selected axes.</InfoBox>}
        </Panel>
      )}

      {/* k-MEANS */}
      {tab==='kmeans'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="k-MEANS++ CLUSTERING" accent="var(--gold)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              k-means++ uses smart centroid initialization (probabilistic seeding based on distance) to avoid poor local minima.
              Iterates: assign each point to nearest centroid → recompute centroids → repeat until convergence.
            </div>
            <div style={{display:'flex',gap:'16px',alignItems:'flex-end',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>k = {k} clusters</div>
                <input type="range" min={2} max={8} value={k} onChange={e=>setK(parseInt(e.target.value))} style={{width:'200px',accentColor:'var(--gold)'}}/>
              </div>
              <Btn onClick={()=>run('k-Means++',()=>{
                if(featureCols.length<2){log('Select at least 2 features','err');return;}
                const r=kMeans(rows,featureCols,k);
                if(!r){log('Failed','err');return;}
                setKResult(r);
                setKScatter(buildScatter(r));
                log(`k=${k}: inertia=${r.inertia} silhouette=${r.silScore} | ${r.n} points`,'ok');
              })} disabled={running}>{running?'CLUSTERING...':'RUN k-MEANS++ →'}</Btn>
            </div>
          </Panel>

          {kResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <Metric label="k (clusters)" value={kResult.k} color="var(--gold)"/>
                <Metric label="Inertia (WCSS)" value={kResult.inertia.toFixed(0)} color="var(--orange)" sub="lower = tighter"/>
                <Metric label="Silhouette" value={kResult.silScore} color={kResult.silScore>0.5?'var(--green)':kResult.silScore>0.25?'var(--gold)':'var(--red)'} sub="-1..+1 (higher=better)"/>
                <Metric label="Points" value={kResult.n} color="var(--t2)"/>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <ClusterScatter scatterData={kScatter} title="CLUSTER SCATTER" color="var(--gold)" xLabel={featureCols[axisX]} yLabel={featureCols[axisY]} axisX={axisX} axisY={axisY} featureCols={featureCols} onAxisChange={handleAxisChange}/>
                <Panel title="CLUSTER SIZE DISTRIBUTION" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={kResult.clusterStats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="cluster" stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`C${v}`}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="size" radius={[2,2,0,0]} name="Points">{kResult.clusterStats.map(cs=><Cell key={cs.cluster} fill={COLORS[cs.cluster%COLORS.length]} opacity={.85}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Panel title="CONVERGENCE" style={{marginTop:'10px'}}>
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart data={kResult.history}>
                        <XAxis dataKey="iter" stroke="var(--t3)" tick={{fontSize:8}}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:8}}/>
                        <Tooltip/>
                        <Line type="monotone" dataKey="inertia" stroke="var(--gold)" strokeWidth={2} dot={false}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                </Panel>
              </div>

              <Panel title="CLUSTER STATS TABLE" accent="var(--gold)">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['Cluster','Size','% of Data','Avg Distance','Centroid (first 3 dims)'].map(h=><th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{kResult.clusterStats.map((cs,i)=>(
                    <tr key={cs.cluster} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                      <td style={{padding:'7px 12px'}}><Tag color={COLORS[cs.cluster%COLORS.length]}>Cluster {cs.cluster}</Tag></td>
                      <td style={{padding:'7px 12px',color:'var(--t1)',fontWeight:600}}>{cs.size}</td>
                      <td style={{padding:'7px 12px',color:'var(--t3)'}}>{(cs.size/kResult.n*100).toFixed(1)}%</td>
                      <td style={{padding:'7px 12px',color:'var(--gold)',fontFamily:'JetBrains Mono'}}>{cs.avgDist}</td>
                      <td style={{padding:'7px 12px',color:'var(--t2)',fontFamily:'JetBrains Mono',fontSize:'10px'}}>{cs.center.slice(0,3).join(', ')}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ELBOW */}
      {tab==='elbow'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ELBOW METHOD + SILHOUETTE ANALYSIS — Find Optimal k" accent="var(--gold)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Runs k-means for k=1…8. The "elbow" where inertia stops decreasing sharply suggests optimal k.
              Silhouette score (valid only k≥2) confirms: peak silhouette = best natural separation.
            </div>
            <Btn onClick={()=>run('Elbow Method',()=>{
              if(featureCols.length<2){log('Select features in Setup tab','err');return;}
              const r=elbowMethod(rows,featureCols);
              setElbowData(r);
              const bestSil=r.filter(d=>d.k>1).reduce((b,d)=>d.silScore>b.silScore?d:b,{k:2,silScore:-1});
              log(`Elbow done. Suggested k=${bestSil.k} (silhouette=${bestSil.silScore})`,'ok');
            })} disabled={running}>{running?'RUNNING k=1..8...':'RUN ELBOW METHOD →'}</Btn>
          </Panel>

          {elbowData.length>0&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="INERTIA vs k — look for the elbow ↙" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={elbowData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="k" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'k (clusters)',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} label={{value:'Inertia',angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="inertia" stroke="var(--gold)" strokeWidth={2.5} dot={{fill:'var(--gold)',r:5}}/>
                    </LineChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Sharp drop then flattens = elbow. That k is optimal. Adding more clusters beyond the elbow gives diminishing returns.</InfoBox>
                </Panel>
                <Panel title="SILHOUETTE SCORE vs k — peak = best k" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={elbowData.filter(d=>d.k>1)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="k" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'k',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={['auto','auto']}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="silScore" stroke="var(--green)" strokeWidth={2.5} dot={{fill:'var(--green)',r:5}}/>
                    </LineChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Silhouette ∈ [−1,+1]. &gt;0.5 = well separated. &lt;0.25 = overlapping clusters. Peak = recommended k.</InfoBox>
                </Panel>
              </div>
              <Panel title="SUMMARY TABLE" accent="var(--gold)">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['k','Inertia','Δ Inertia','Silhouette','Verdict'].map(h=><th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{elbowData.map((d,i)=>{
                    const prev=elbowData[i-1];
                    const delta=prev?-(d.inertia-prev.inertia):null;
                    const bestSil=Math.max(...elbowData.filter(x=>x.k>1).map(x=>x.silScore));
                    return(
                      <tr key={d.k} style={{borderBottom:'1px solid var(--b0)',background:d.silScore===bestSil&&d.k>1?'var(--ggold)':'transparent'}}>
                        <td style={{padding:'7px 12px',color:'var(--gold)',fontWeight:700,fontFamily:'JetBrains Mono'}}>{d.k}</td>
                        <td style={{padding:'7px 12px',color:'var(--t2)'}}>{d.inertia.toFixed(1)}</td>
                        <td style={{padding:'7px 12px',color:delta>0?'var(--green)':'var(--t3)'}}>{delta?`-${delta.toFixed(1)}`:'—'}</td>
                        <td style={{padding:'7px 12px',color:'var(--green)'}}>{d.k>1?d.silScore:'—'}</td>
                        <td style={{padding:'7px 12px'}}>{d.silScore===bestSil&&d.k>1?<Tag color="var(--gold)">★ RECOMMENDED</Tag>:d.k===1?<Tag color="var(--t3)">baseline</Tag>:<span style={{color:'var(--t3)',fontSize:'10px'}}>ok</span>}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* DBSCAN */}
      {tab==='dbscan'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DBSCAN — Density-Based Spatial Clustering" accent="var(--cyan)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              DBSCAN finds clusters of arbitrary shape. Points with ≥minPts neighbors within radius ε are "core points".
              Cluster = all points reachable from a core point. Points unreachable from any core = <strong style={{color:'var(--red)'}}>noise (label −1)</strong>.
              Great for irregular-shaped clusters and outlier detection.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:'16px',alignItems:'flex-end',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>ε (neighborhood radius) = {eps}</div>
                <input type="range" min={0.1} max={5} step={0.1} value={eps} onChange={e=>setEps(parseFloat(e.target.value))} style={{width:'100%',accentColor:'var(--cyan)'}}/>
                <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>↑ larger = more points per cluster, fewer noise</div>
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>minPts (min cluster size) = {minPts}</div>
                <input type="range" min={2} max={20} step={1} value={minPts} onChange={e=>setMinPts(parseInt(e.target.value))} style={{width:'100%',accentColor:'var(--cyan)'}}/>
                <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>↑ larger = denser clusters, more noise</div>
              </div>
              <Btn onClick={()=>run('DBSCAN',()=>{
                if(featureCols.length<2){log('Select features','err');return;}
                const r=dbscan(rows,featureCols,eps,minPts);
                if(!r){log('Failed','err');return;}
                setDbResult(r);
                setDbScatter(buildScatter(r));
                log(`DBSCAN: ${r.clusterCount} clusters · ${r.noiseCount} noise (${r.noisePct}%)`,'ok');
              })} color="var(--cyan)" disabled={running}>{running?'SCANNING...':'RUN DBSCAN →'}</Btn>
            </div>
          </Panel>

          {dbResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <Metric label="Clusters Found" value={dbResult.clusterCount} color="var(--cyan)"/>
                <Metric label="Noise Points" value={dbResult.noiseCount} color="var(--red)" sub={`${dbResult.noisePct}% of data`}/>
                <Metric label="Clustered" value={dbResult.n-dbResult.noiseCount} color="var(--green)"/>
                <Metric label="Total Points" value={dbResult.n} color="var(--t2)"/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <ClusterScatter scatterData={dbScatter} title="DBSCAN CLUSTERS (gray = noise)" color="var(--cyan)" showNoise xLabel={featureCols[axisX]} yLabel={featureCols[axisY]} axisX={axisX} axisY={axisY} featureCols={featureCols} onAxisChange={handleAxisChange}/>
                <Panel title="CLUSTER SIZES" accent="var(--cyan)">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={[...dbResult.clusterSizes,{cluster:'noise',size:dbResult.noiseCount}]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="cluster" stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>v==='noise'?'NOISE':`C${v}`}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="size" radius={[2,2,0,0]}>{[...dbResult.clusterSizes,{cluster:'noise'}].map(cs=><Cell key={cs.cluster} fill={cs.cluster==='noise'?'var(--t3)':COLORS[cs.cluster%COLORS.length]} opacity={.85}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Tuning: if too many noise points → increase ε or decrease minPts. If all one cluster → decrease ε.</InfoBox>
                </Panel>
              </div>
            </>
          )}
        </div>
      )}

      {/* HIERARCHICAL */}
      {tab==='hierarchical'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="HIERARCHICAL AGGLOMERATIVE CLUSTERING" accent="var(--violet)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Bottom-up merging: starts with each point as its own cluster, then greedily merges the two closest clusters.
              Uses <strong style={{color:'var(--violet)'}}>single linkage</strong> (min distance between any two points).
              The dendrogram shows merge order. Cuts at different heights = different k values. (Capped at 100 pts for performance.)
            </div>
            <Btn onClick={()=>run('Hierarchical Clustering',()=>{
              if(featureCols.length<2){log('Select features','err');return;}
              const r=hierarchical(rows,featureCols,5);
              if(!r){log('Failed','err');return;}
              setHResult(r);
              setHScatter(buildScatter(r));
              log(`Hierarchical: ${r.clusterCount} clusters · ${r.dendrogram.length} merge steps`,'ok');
            })} color="var(--violet)" disabled={running}>{running?'MERGING...':'RUN HIERARCHICAL →'}</Btn>
          </Panel>

          {hResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <ClusterScatter scatterData={hScatter} title="CLUSTER SCATTER" color="var(--violet)" xLabel={featureCols[axisX]} yLabel={featureCols[axisY]} axisX={axisX} axisY={axisY} featureCols={featureCols} onAxisChange={handleAxisChange}/>
                <Panel title="DENDROGRAM — Merge Distance per Step" accent="var(--violet)">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={hResult.dendrogram}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="step" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'Merge Step →',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} label={{value:'Distance',angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}}/>
                      <Tooltip/>
                      <Area type="monotone" dataKey="dist" stroke="var(--violet)" fill="var(--violet)" fillOpacity={.15} strokeWidth={2} name="Merge Distance"/>
                    </AreaChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Large jumps in merge distance = natural cluster boundaries. Cut below the biggest jump for best k.</InfoBox>
                </Panel>
              </div>
              <Panel title="CLUSTER SIZES" accent="var(--violet)">
                <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                  {hResult.clusterSizes.map((cs,i)=>(
                    <div key={cs.cluster} style={{padding:'10px 16px',background:COLORS[cs.cluster%COLORS.length]+'18',border:`1px solid ${COLORS[cs.cluster%COLORS.length]}44`,borderRadius:'3px',textAlign:'center'}}>
                      <div style={{fontSize:'10px',color:'var(--t3)'}}>Cluster {cs.cluster}</div>
                      <div style={{fontSize:'20px',fontWeight:800,color:COLORS[cs.cluster%COLORS.length]}}>{cs.size}</div>
                      <div style={{fontSize:'9px',color:'var(--t3)'}}>{(cs.size/Math.min(100,rows.length)*100).toFixed(0)}%</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* INSIGHTS */}
      {tab==='insights'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          {[
            {title:'k-MEANS',color:'var(--gold)',items:[
              ['Best for','Spherical clusters of similar size'],
              ['Sensitivity','Initial centroids (k-means++ fixes this)'],
              ['Must specify','k in advance'],
              ['Time complexity','O(n·k·d·i) — fast'],
              ['Metric','Inertia (WCSS) · Silhouette'],
              ['Weakness','Fails with non-convex or different-density clusters'],
            ]},
            {title:'DBSCAN',color:'var(--cyan)',items:[
              ['Best for','Arbitrary-shaped clusters, outlier detection'],
              ['Sensitivity','ε and minPts parameters'],
              ['Must specify','ε (radius) and minPts'],
              ['Time complexity','O(n·log n) with index, O(n²) naive'],
              ['Metric','Noise %, cluster count validation'],
              ['Weakness','High-dimensional data (curse of dimensionality)'],
            ]},
            {title:'HIERARCHICAL',color:'var(--violet)',items:[
              ['Best for','Unknown k, nested/hierarchical structure'],
              ['Sensitivity','Linkage method (single/complete/ward)'],
              ['Must specify','Nothing upfront — cut tree post-hoc'],
              ['Time complexity','O(n²) — slow on large datasets'],
              ['Metric','Dendrogram jump distance'],
              ['Weakness','Computationally expensive, no reassignment'],
            ]},
            {title:'CHOOSING ALGORITHM',color:'var(--green)',items:[
              ['<1k points','Try all three'],
              ['Spherical clusters','k-Means is fast and accurate'],
              ['Unknown k','Elbow + silhouette or hierarchical'],
              ['Outlier detection','DBSCAN'],
              ['Large datasets (>50k)','k-Means only'],
              ['Noisy real-world data','DBSCAN or k-Means with outlier removal'],
            ]},
          ].map(({title,color,items})=>(
            <Panel key={title} title={title} accent={color}>
              {items.map(([k,v])=>(
                <div key={k} style={{display:'flex',gap:'10px',padding:'7px 0',borderBottom:'1px solid var(--b0)'}}>
                  <div style={{width:'120px',fontSize:'10px',color:'var(--t3)',flexShrink:0}}>{k}</div>
                  <div style={{fontSize:'11px',color:'var(--t1)'}}>{v}</div>
                </div>
              ))}
            </Panel>
          ))}
        </div>
      )}

      {!['upload'].includes(tab)&&<LogPanel logs={logs} height={55}/>}
    </div>
  );
}
